import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import type { ZodTypeAny } from 'zod'
import Column from '@app/components/layout/Column'
import { Form } from '@app/components/core/form/Form'
import { TextField } from '@app/components/core/form/fields/TextField'
import { TextAreaField } from '@app/components/core/form/fields/TextAreaField'
import { ToggleField } from '@app/components/core/form/fields/ToggleField'
import { DateTimeSplitField } from '@app/components/core/form/fields/DateTimeSplitField'
import { CreationGuide } from '@app/pages/dashboard/initiative/create/blocks/CreationGuide'
import { useTranslations } from '@i18n/useLanguage'
import { type GeneralInfoFormValues } from '../shared/eventValidation'
import { eventCreationSteps, type EventCreationStep } from '../shared/workflow'
import { StepValuesSync } from '../shared/StepValuesSync'
import { Toggle } from '@app/components/core/toggle/Toggle'
import CalendarIllustration from '@app/assets/images/illustrations/calendar.png'
import { Image } from '@app/components/core/image/Image'

const CURRENT_STEP: EventCreationStep = eventCreationSteps.generalInformation

type Props = {
  schema: ZodTypeAny
  defaultValues: GeneralInfoFormValues
  onValuesChange: (values: GeneralInfoFormValues) => void
}

/**
 * Date/time section: a one-day / multiday toggle that reveals the end field.
 * Both fields write directly to the `start` / `end` form values via
 * DateTimeSplitField, so the data model stays a pair of datetime strings.
 */
const EventDateTimeFields = ({ hasEnd }: { hasEnd: boolean }) => {
  const t = useTranslations().app.dashboard.event.creation
  const { setValue } = useFormContext<GeneralInfoFormValues>()
  const [isMultiday, setIsMultiday] = useState(hasEnd)

  const durationOptions = [
    { label: t.singleDay, value: false },
    { label: t.multiDay, value: true },
  ]

  return (
    <Column gap="1.5rem" alignItems="stretch">
      <Toggle<boolean>
        style={{ alignSelf: 'stretch' }}
        options={durationOptions}
        selectedOption={isMultiday}
        setSelectedOption={(value) => {
          setIsMultiday(value)
          if (!value) {
            setValue('end', '', { shouldValidate: true })
          }
        }}
      />
      <DateTimeSplitField<GeneralInfoFormValues>
        name="start"
        dateLabel={t.dateStartLabel}
        datePlaceholder={t.dateStartPlaceholder}
        timeLabel={t.startTimeLabel}
        timePlaceholder={t.startTimePlaceholder}
      />
      {isMultiday && (
        <DateTimeSplitField<GeneralInfoFormValues>
          name="end"
          dateLabel={t.dateEndLabel}
          datePlaceholder={t.dateEndPlaceholder}
          timeLabel={t.endTimeLabel}
          timePlaceholder={t.endTimePlaceholder}
        />
      )}
    </Column>
  )
}

export const GeneralInformationStep = ({
  schema,
  defaultValues,
  onValuesChange,
}: Props) => {
  const t = useTranslations().app.dashboard.event.creation

  const stepTitles = { [CURRENT_STEP]: t.steps.generalInformation.title }
  const stepDescriptions = {
    [CURRENT_STEP]: t.steps.generalInformation.description,
  }

  const eventTypeOptions = [
    { label: t.groupEvent, value: true },
    { label: t.oneOnOne, value: false },
  ]

  return (
    <CreationGuide
      steps={[CURRENT_STEP]}
      currentStep={CURRENT_STEP}
      stepTitles={stepTitles}
      stepDescriptions={stepDescriptions}
      illustration={
        <Image
          src={CalendarIllustration}
          alt="Calendar Illustration"
          style={{ maxWidth: '15rem' }}
        />
      }
    >
      <Form<GeneralInfoFormValues>
        schema={schema}
        defaultValues={defaultValues}
        mode="onChange"
        onSubmit={() => undefined}
      >
        <Column gap="2rem" alignItems="stretch" style={{ marginTop: '1.5rem' }}>
          <StepValuesSync<GeneralInfoFormValues>
            onValuesChange={onValuesChange}
          />
          <ToggleField
            options={eventTypeOptions}
            name="isGroupEvent"
            label={t.isGroupEventLabel}
          />
          <TextField<GeneralInfoFormValues>
            name="title"
            label={t.titleLabel}
            placeholder={t.titlePlaceholder}
          />
          <TextField<GeneralInfoFormValues>
            name="intro"
            label={t.introLabel}
            placeholder={t.introPlaceholder}
          />
          <TextAreaField<GeneralInfoFormValues>
            name="content"
            label={t.contentLabel}
            placeholder={t.contentPlaceholder}
          />
          <EventDateTimeFields hasEnd={Boolean(defaultValues.end)} />
        </Column>
      </Form>
    </CreationGuide>
  )
}
